/**
 * The pinned bar's pins (job page v2): the office default for everyone, and
 * each person's own list once they change it, saved server-side in
 * user_preferences (the same per-user store as "Try the new job page"), so
 * it follows them to any computer. Reset puts them back on the office
 * default (the saved value becomes null).
 */
import { toast } from "sonner";
import { useUserPreference } from "@/lib/user-preference";
import {
    JOB_PINS_PREF_KEY,
    OFFICE_DEFAULT_PINS,
    normalizeJobPins,
    sameAsOfficeDefault,
    togglePin,
    type JobActionId,
} from "./jobActions";

export interface JobPins {
    pins: JobActionId[];
    /** True while on the office default (nothing saved, or saved = default). */
    isOfficeDefault: boolean;
    toggle: (id: JobActionId) => void;
    reset: () => void;
}

export function useJobPins(): JobPins {
    const [saved, setSaved] = useUserPreference<unknown>(JOB_PINS_PREF_KEY, null, {
        onError: () => toast.error("Your pins didn't save. Try again."),
    });
    const own = normalizeJobPins(saved);
    const pins = own ?? OFFICE_DEFAULT_PINS;
    return {
        pins,
        isOfficeDefault: own === null || sameAsOfficeDefault(own),
        toggle: (id) => setSaved(togglePin(pins, id)),
        reset: () => setSaved(null),
    };
}
